import {
  clientsOpportunitiesListOptions,
  ReturnOpportunityReadable
} from '@crm/api'
import { CreateOpportunityModal, OpportunityStatuses } from '@crm/components'
import { OpportunityActionsButton } from '@crm/components/OpportunityInfo/OpportunityActionsButton'
import { OpportunitiesActionsModals } from '@crm/components/OpportunityInfo/OpportunityActionsModals'
import { ModalState } from '@crm/components/OpportunityInfo/types'
import { composePrice } from '@crm/utils'
import { ConditionalText, DayLabel, TableContainer } from '@shared/components'
import { useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { displayDate } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Button, Flex, TableColumnsType } from 'antd'
import { Add } from 'iconsax-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

export const ClientOpportunitiesCol = () => {
  const { t } = useLocales()
  const { id } = useParams()
  const params = useQueryParams()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalState, setModalState] = useState<ModalState>({
    opportunity: null,
    type: null
  })

  const { data, isLoading } = useQuery({
    ...clientsOpportunitiesListOptions({
      path: { client_id: id! },
      query: { ...params }
    }),
    enabled: !!id
  })

  const onCell = () => ({
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.MouseEvent) => e.stopPropagation()
  })

  const columns: TableColumnsType<ReturnOpportunityReadable> = [
    {
      title: t('lead.lead_name'),
      render: (_, record) => (
        <ConditionalText condition={!!record?.lead?.name}>
          {record?.lead?.name}
        </ConditionalText>
      ),
      key: 'name'
    },
    {
      title: t('opportunities.value'),
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => (
        <ConditionalText condition={!!value}>
          {composePrice(value, record?.currency?.symbol)}
        </ConditionalText>
      )
    },
    {
      title: t('task.product'),
      dataIndex: ['product', 'name'],
      key: 'product',
      render: (product) => (
        <ConditionalText condition={!!product}>{product}</ConditionalText>
      )
    },
    {
      title: t('opportunities.responsible'),
      dataIndex: ['owner', 'name'],
      key: 'owner'
    },
    {
      title: t('filter.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt) => <DayLabel date={createdAt} />
    },
    {
      title: t('task.close_date'),
      dataIndex: 'close_date',
      key: 'close_date',
      render: (closeDate) => (closeDate ? displayDate(closeDate) : '-')
    },
    {
      title: t('task.status'),
      onCell,
      render: (_, opportunity) => (
        <OpportunityStatuses opportunity={opportunity} />
      )
    },
    {
      title: t('refusal_reason'),
      dataIndex: ['reason', 'name'],
      key: 'reason_name',
      render: (reasonName) => (
        <ConditionalText condition={!!reasonName}>{reasonName}</ConditionalText>
      )
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      onCell,
      render: (_, opportunity) => (
        <OpportunityActionsButton
          opportunity={opportunity}
          setModalState={setModalState}
        />
      )
    }
  ]

  return (
    <Flex vertical gap={16}>
      <TableContainer
        rowKey="id"
        columns={columns}
        data={data}
        loading={isLoading}
        rightChildren={
          <Button
            type="primary"
            icon={<Add size={16} />}
            onClick={() => setIsModalOpen(true)}
          >
            {t('opportunity.add_opportunity')}
          </Button>
        }
      />

      {isModalOpen && (
        <CreateOpportunityModal
          clientId={id}
          open
          onCancel={() => setIsModalOpen(false)}
          onSuccess={() => setIsModalOpen(false)}
        />
      )}

      <OpportunitiesActionsModals
        modalState={modalState}
        setModalState={setModalState}
      />
    </Flex>
  )
}
